import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { Router } from '@angular/router';
import { countryConfig } from 'src/country-config/country-config';
import { OrderService } from '../../../networking/services/order/order.service';
import { LocalStorageManager } from '../../local-storage/local-storage.manager';
import { CustomerManager } from '../../customer/customer/customer.manager';
import { LocationManager } from '../../location/location.manager';
import { AddressManager } from '../../customer/address/address.manager';
import { DeleteCartAction } from '../../../actions/cart.actions';
import { GoogleTagService } from '../../../networking/services/shared/google-tag/google-tag.service';
import { CallCenterManager } from '../../call-center/call-center.manager';
import { SessionStorageManager } from '../../session-storage/session-storage.manager';
import { DeliveryTypeEnum } from '../../../networking/models/location/enum/delivery-type.enum';
import { UpdateScanAndGoFirebaseStatusAction } from '../../../actions/scan-and-go.actions';
import { Product } from '../../../networking/models/product/responses/product.class';
import { MobileConnectorService } from 'src/app/data/networking/services/shared/mobile-connector/mobile-connector.service';
import { TalonOneService } from 'src/app/data/networking/services/talon-one/talon-one.service';
import { isPlatformBrowser } from '@angular/common';
import { SourceEnum } from 'src/app/data/networking/models/base-service/enum/base-service.enum';
import { EstimatedTimeResponse, GetActiveOrdersResponse, GetPreviousOrdersBody, GetPreviousOrdersResponse } from 'src/app/data/networking/models/order/responses/order-res';
import { UploadImgResponseInterface } from 'src/app/data/networking/models/legal/responses/legal-res';
import {DeliveryTypeTimeEvent} from 'src/app/ui/shared/event-listeners/delivery-type-time/delivery-type-time.event';
import {DeliveryTypeTimeResponse} from 'src/app/data/networking/models/delivery-type-time/delivery-type-time.model';

@Injectable({
  providedIn: 'root',
})
export class OrderManager {
  tokenMP;
  itemPurchase;
  public firstPurchaseValidate: boolean;
  public source: string = 'WEB';
  public isBrowser: boolean;

  constructor(
    private localStorageManager: LocalStorageManager,
    private ssm: SessionStorageManager,
    private orderService: OrderService,
    private customerManager: CustomerManager,
    private locationManager: LocationManager,
    private addressManager: AddressManager,
    private gtmService: GoogleTagService,
    private callCenterManager: CallCenterManager,
    private mcService: MobileConnectorService,
    private store: Store,
    private router: Router,
    private talonOne: TalonOneService,
    private deliveryTypeTimeEvent: DeliveryTypeTimeEvent,
    @Inject(PLATFORM_ID) platformId,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (this.isBrowser) this.source = window?.screen?.width <= 991 ? SourceEnum.RESPONSIVE : SourceEnum.WEB;
  }

  create(rawData?, mercadoPago?): Observable<any> {
    this.firstPurchaseValidate = this.localStorageManager.has('firstPurchaseValidate') ? this.localStorageManager.get('firstPurchaseValidate') : false;
    const isSaG = this.localStorageManager.get('mobileSaG');

    if (!rawData && isSaG) {
      rawData = this.store?.snapshot().scanAndGoStore?.order;
    }

    if (!rawData?.payment?.id) {
      this.router.navigate(['/checkout']);
      return of({});
    }
    const customerPagoMovil = rawData.customerPagoMovil || this.ssm.get('currentPM');
    const dollars = rawData.dollars || null;
    const {
      cart,
      orderDetails,
      payment,
      farmaCredits,
      shippingCostItemsMarkeplaceRequest,
      isOrderProvider
   } = rawData;
    const address = this.addressManager.getDeliveryAddress();
    const user = this.customerManager.customer;
    const itemList = cart.carts
      .flatMap(orderCart => orderCart.itemList
        .filter(item => parseInt(item.id, 10) > 0)
        .map(item => ({id: item.id, quantitySold: item.quantitySold})));

    let products = [];
    itemList
      .sort((a, b) => a.id - b.id)
      .forEach(product => {
        const currentProduct = products.filter(i => i && i.id === product.id)[0];
        if (!currentProduct) {
          products = [...products, product];
        } else {
          products.forEach(i => {
            if (i.id === product.id) {
              i.quantitySold += product.quantitySold;
            }
            return i;
          });
        }
      });

    let pickingDate;
    const expressCart = cart.carts.filter(c => c.isExpress)[0];
    if (expressCart) {
      pickingDate = this.ssm.get('pickingDate')?.timeStamp;
    }

    const callCenterCustomer = this.callCenterManager.getCustomer();
    const paymentCardId = payment?.creditCardId || 0;
    let data: any = {
      addressDetails: address?.comments || '',
      customerPhone: user.phone,
      deliveryType: isSaG ? DeliveryTypeEnum.SCANANDGO : this.locationManager.getDeliveryType(),
      highDemand: cart.isHighDemand,
      idFarmatodo: callCenterCustomer ? callCenterCustomer.id : user.id,
      idStoreGroup: isSaG ? this.getSaGStoreId() : this.locationManager.getIdStoreGroup(),
      itemList,
      orderDetails,
      paymentType: {id: payment.id},
      source: countryConfig.generalEnv.source,
      paymentCardId: payment.creditCardId && !Number.isNaN(payment.creditCardId) ? payment.creditCardId : payment.id || 0,
      quotas: parseInt(payment.id, 10) === 3 ? payment.quotas : 1,
      talonOneData: {
        paymentCardId,
        storeId: user.idStoreGroup,
        source: this.source,
        isCreateOrder: true,
        deliveryType: this.locationManager.getDeliveryType(),
      },
      deliveryHome: rawData.deliveryHome,
    };

    if (farmaCredits) {
      data.talonOneData.farmaCredits = farmaCredits;
    }
    if(isOrderProvider){
      data.deliveryType = DeliveryTypeEnum.MARKETPLACE;
    }
    if(shippingCostItemsMarkeplaceRequest){
      data.shippingCostItemsMarkeplaceRequest = shippingCostItemsMarkeplaceRequest;
    }
    if (customerPagoMovil) {
      data.customerPagoMovil = customerPagoMovil;
    }
    if (dollars != null) {
      data.dollars = dollars;
    }
    if (payment.id === '6') {
      data.financialInstitutions = rawData.financialInstitutions;
      data.typePersonPSE = rawData.typePersonPSE;
      data.identification = rawData.identification;
    }

    data.idAddress = address?.idAddress;

    if (callCenterCustomer) {
      data.customerIdCallCenter = user.id;
      data.customerPhone = callCenterCustomer.phone;
    }

    if (rawData.payment.creditCard?.creditCardNumber && rawData.isMercadoPago) {
      data = {
        ...data,
        creditCardToken: mercadoPago.id,
      };
    }

    if (pickingDate) {
      data = {
        ...data,
        pickingDate,
      };
    }

    if (farmaCredits) {
      data = {
        ...data,
        farmaCredits,
      };
    }

    const estimatedTime: EstimatedTimeResponse = this.ssm.get('estimatedTime');
    if (estimatedTime && !pickingDate) {
      data.estimatedRange = estimatedTime?.estimatedRange;
      data.estimatedMinutes = estimatedTime?.estimatedMinutes;
    }

    if (pickingDate) {
      const scheduleDate = pickingDate?.replace('T', ' ');
      data.scheduleDate = scheduleDate;
    }

    const idCustomerWebSafe = callCenterCustomer ? callCenterCustomer.idCustomerWebSafe : this.customerManager.customer.idCustomerWebSafe;

    if (data.paymentType.id.toString() === '2') {
      this.store.dispatch(new UpdateScanAndGoFirebaseStatusAction('ORDER_LOADED')).subscribe();
    }
    const optimalRouteOptionSelected = this.localStorageManager.get('optimalRouteOptionSelected');
    if (optimalRouteOptionSelected) {
      data.idOptimalRoute = optimalRouteOptionSelected.popupId;
      data.optionSelectedPopUp = optimalRouteOptionSelected.optionSelectPopup;
    }

    return this.orderService.create(data, {idCustomerWebSafe})
      .pipe(tap((order: any) => {

        this.addressManager.selectAddressDefault(data?.idAddress);

        if (order?.statusCode >= 400 || !order?.id) return;

        if (!this.mcService.isMobile) {
          order.firstPurchase = this.firstPurchaseValidate;
          this.gtmService.onPurchase(cart, {...data, ...order}, this.customerManager.customer);
          this.sendPurchase(cart, itemList, {...data, ...order}, this.customerManager.customer);
          this.sendItemPurchase(cart, this.customerManager.customer);

          this.localStorageManager.removeItem('firstPurchaseValidate');
          this.localStorageManager.removeItem('optimalRouteOptionSelected');
        } else {
          this.mcService.logPurchase(order.id);
        }

        this.store.dispatch(new DeleteCartAction()).subscribe();

        if (callCenterCustomer) {
          this.callCenterManager.logOut();
        }
      }));
  }

  getSaGStoreId() {
    if (!this.store?.snapshot().scanAndGoStore?.config) return countryConfig.defaultStoreId;
    return this.store.snapshot().scanAndGoStore.config.storeId;
  }

  getOrder(params): Observable<any> {
    const user = this.customerManager.customer;
    if(user?.customerId || !user?.isAnonymous ) {
    const dataSend = {
      idOrder: params,
      tokenIdWebSafe: user.token.tokenIdWebSafe,
      idCustomerWebSafe: user.idCustomerWebSafe,
      token: user.token.token,
    };

    return this.orderService.getSingle(dataSend)
      .pipe(
        map((response) => {
          const formattedItemList = response?.itemList
            ?.filter((item) => !item?.coupon)
            ?.map((item) => new Product(item))
            || [];
          return { ...response, itemList: formattedItemList }
        })
      );
    }
  }

  getTracking(params): Observable<any> {
    const user = this.customerManager.customer;
    const data = {
      ...params,
      tokenIdWebSafe: user.token.tokenIdWebSafe,
      token: user.token.token,
      idCustomerWebSafe: user.idCustomerWebSafe,
    };
    return this.orderService.getTracking(data);
  }

  getOrders(data?): Observable<any> {
    const user = this.customerManager.customer;
    if (user && !user.isAnonymous) {
      const requestData: any = {
        ...data,
        idCustomerWebSafe: user.idCustomerWebSafe,
        tokenIdWebSafe: user.token?.tokenIdWebSafe,
        token: user.token?.token,
        idCustomer: user.id,
      };
      
      return this.orderService.getAll(requestData);
    }
    return of(null);
  }

  rateOrder(data): Observable<any> {
    return this.orderService.rate(data);
  }

  reOrder(data): Observable<any> {
    const user = this.customerManager.customer;
    const params = {
      idStoreGroup: this.locationManager.getIdStoreGroup(),
      idCustomerWebSafe: user.idCustomerWebSafe,
    };
    return this.orderService.reOrder(data, params);
  }

  getRatings(): Observable<any> {
    const user = this.customerManager.customer;
    const data = {
      tokenIdWebSafe: user.token.tokenIdWebSafe,
      token: user.token.token,
    };
    return this.orderService.getRatings(data);
  }

  putQualifyService(data): Observable<any> {
    return this.orderService.rate(data);
  }

  getReviewLastOrderId() {
    return this.ssm.get('reviewLastOrderId');
  }

  setReviewLastOrderId(orderId: string) {
    return this.ssm.set('reviewLastOrderId', orderId);
  }

  putOrder(data) {
    return this.orderService.updateOrder(data);
  }

  updateUserPrime( data ): Observable<any>{
    return this.orderService.updateUserPrime(data);
  }
  sendPurchase(cart, item, order, customer) {
    let coupon = '';
    item ?
      item.filter(res => {
        if (res?.coupon === true) {
          coupon = res?.description;
        }
      }) : coupon = '';
    let purchaseSumary = {
      "profileId": customer?.analyticsUUID ? customer?.analyticsUUID : 'anonymous',
      "idCustomerWebSafe": customer?.idCustomerWebSafe,
      "token": customer?.token?.token,
      "tokenIdWebSafe": customer?.token?.tokenIdWebSafe,
      "type": "Purchase_summary",
      "attributes": {
        "coupon": coupon,
        "delivery_type": order?.deliveryType,
        "discount": cart?.discountRate ? cart?.discountRate : 0,
        "order_id": order?.id,
        "payment_method": order?.paymentType?.id,
        "prime_saving": cart?.savingPrime,
        "total_price": cart?.totalPrice,
        "total_quantity": cart?.totalQuantity
      },
      "responseContent": [
        "triggeredCampaigns",
        "customerProfile"
      ]
    }
    this.talonOne.sendEventTalonOne(purchaseSumary).subscribe();

  }

  sendItemPurchase2(cart, customer) {
    cart?.carts.filter(res => {
      res?.itemList?.forEach((product, j) => {
        let itemPurchase = {
          "idCustomerWebSafe": customer?.idCustomerWebSafe,
          "token": customer?.token?.token,
          "tokenIdWebSafe": customer?.token?.tokenIdWebSafe,
          "profileId": customer?.analyticsUUID ? customer?.analyticsUUID : 'anonymous',
          "type": "Item_purchased",
          "attributes": {
            "prime_price": product?.primePrice ? product.primePrice : 0,
            "brand": product?.marca ? product.marca : '',
            "discount": product?.discount ? product.discount : 0,
            "item_category": product?.categorie ? product.categorie : '',
            "item_category2": product?.subCategory ? product.subCategory : '',
            "item_department": product?.departments ? product.department[0] : '',
            "item_id": product?.id.toString(),
            "item_name": product?.description ? product.description : '',
            "item_name_first_word": product?.description.charAt(0),
            "item_price": product?.fullPrice ? product.fullPrice : 0,
            "item_quantity": product?.quantitySold ? product.quantitySold : 0,
            "item_rms_class": product?.rms_class ? product.rms_class : '',
            "item_rms_deparment": product?.rms_deparment ? product.rms_deparment : '',
            "item_rms_group": product?.rms_group ? product.rms_group : '',
            "item_rms_subclass": product?.rms_subclass ? product.rms_subclass : '',
          },
          "responseContent": [
            "triggeredCampaigns",
            "customerProfile"
          ]
        }
        this.talonOne.sendEventTalonOne(itemPurchase).subscribe(res => console.log(res))
      });
    });
  }

  sendItemPurchase(cart, customer) {
    this.itemPurchase = {
      "idCustomerWebSafe": customer?.idCustomerWebSafe,
      "token": customer?.token?.token,
      "tokenIdWebSafe": customer?.token?.tokenIdWebSafe,
      "profileId": customer?.analyticsUUID ? customer?.analyticsUUID : 'anonymous',
      "type": "Item_purchased",
      "attributes": [],
      "responseContent": [
        "triggeredCampaigns",
        "customerProfile"
      ]
    }
    cart?.carts.filter(res => {
      res?.itemList?.forEach((product, j) => {
        let productDetail = {
          "prime_price": product?.primePrice ? product.primePrice : 0,
          "brand": product?.marca ? product.marca : '',
          "discount": product?.discount ? product.discount : 0,
          "item_category": product?.categorie ? product.categorie : '',
          "item_category2": product?.subCategory ? product.subCategory : '',
          "item_department": product?.departments?.length ? product.departments[0] : '',
          "item_id": product?.id.toString(),
          "item_name": product?.description ? product.description : '',
          "item_name_first_word": product?.description.charAt(0),
          "item_price": product?.fullPrice ? product.fullPrice : 0,
          "item_quantity": product?.quantitySold ? product.quantitySold : 0,
          "item_rms_class": product?.rms_class ? product.rms_class : '',
          "item_rms_deparment": product?.rms_deparment ? product.rms_deparment : '',
          "item_rms_group": product?.rms_group ? product.rms_group : '',
          "item_rms_subclass": product?.rms_subclass ? product.rms_subclass : '',
        }
        this.itemPurchase.attributes.push(productDetail);
      });
    });
    this.talonOne.sendEventTalonOne2(this.itemPurchase).subscribe(res => console.log(res))
  }
  gtmCheckoutContentCardPrime(data){
    this.gtmService.checkoutContentCardPrime(data)
  }

  getEstimatedTime(idAddress?: number, orderId?: number): Observable<EstimatedTimeResponse> {
    return this.orderService.getEstimatedTime(idAddress, orderId)
      .pipe(tap(response => {
        this.ssm.set('estimatedTime', response);
      }));
  }

  getActiveOrders(): Observable<GetActiveOrdersResponse> {
    const body = {
      idCustomerWebSafe: this.customerManager?.customer?.idCustomerWebSafe,
      cursor: null,
    };
    return this.orderService.getActiveOrders(body);
  }

  getPreviousOrders(data?:GetPreviousOrdersBody): Observable<GetPreviousOrdersResponse> {
    const body = {
      idCustomerWebSafe: this.customerManager?.customer?.idCustomerWebSafe,
      cursor: null,
      ...data,
    };
    return this.orderService.getPreviousOrders(body);
  }

  uploadPrescription( prescriptionData ):Observable<UploadImgResponseInterface> {
    return this.orderService.uploadPrescription(prescriptionData);
  }

  getDeliveryTypeTimeFromService(citySelected?: string): Observable<DeliveryTypeTimeResponse> {
    const idCustomerWebSafe = this.customerManager?.customer?.idCustomerWebSafe;
    const deliveryAddressCity = this.addressManager.getDeliveryAddress()?.city;
    const cityLocationManager = this.locationManager.city?.id;
    const city = citySelected
      || deliveryAddressCity
      || cityLocationManager
      || countryConfig.defaultCity;
    const body = {
      idCustomerWebSafe,
      city,
    };

    return this.orderService.getDeliveryTypeTime(body)
      .pipe(
        tap(response => { this.deliveryTypeTimeEvent.update(response); }),
      );
  }

  getDeliveryTypeTime(): Observable<DeliveryTypeTimeResponse> {
    return this.deliveryTypeTimeEvent.get;
  }
}
